import TealGreyButton from '@components/button/TealGreyButton'
import SentiusCard from '@components/card/SentiusCard'
import Column from '@components/layout/Column'
import Container from '@components/layout/Container'
import Row from '@components/layout/Row'
import Loader from '@components/loader/Loader'
import PageSeo from '@components/seo/PageSeo'
import FixedBackground from '@components/shapes/FixedBg'
import LeftRightBg from '@components/shapes/LeftRightBg'
import useMediaQuery, { maxWidthQueries } from '@operations/utils/useMediaQuery'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import AdvancedSearch from './AdvancedSearch'
import SearchForm from './SearchForm'
import randomizeSentius from '@operations/utils/randomizeSentius'
export const SORT_SENTIUS = '_sort=number:ASC'
export const SENTIUS_LIMIT = 20
export const ALPHA_ONLY_REGEX = /^[a-zA-Z]+$/
export const NUMERIC_ONLY_REGEX = /^[0-9]*$/

const TestCardHolder = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  margin-top: 2rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  width: 100%;
  height: 100%;
  @media (max-width: 820px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }
  @media (max-width: 641px) {
    flex-direction: column;
  }
`

const T2 = styled.div`
  display: flex;
  flex-direction: row;
  @media (max-width: 820px) {
    display: flex;
    width: 50%;
    flex-wrap: wrap;
    width: auto;
    align-items: center;
    justify-content: center;
    flex-direction: row;
    margin: 50px;
  }
  @media (max-width: 641px) {
    margin: 10px;
  }
`

const HeaderImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
  object-position: center;
  margin-bottom: 2rem;
  margin-top: 2rem;
`
const SideGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  box-shadow: -10px 0 10px -10px white, 10px 0 10px -10px white;
  animation: pulse 3s ease-in-out infinite alternate;

  @keyframes pulse {
    0% {
      box-shadow: -10px 0 10px -5px white, 10px 0 10px -5px white;
    }
    100% {
      box-shadow: -20 0 20px -10px white, 20px 0 20px -10px white;
    }
  }
`
const Sentipedia = ({ dynasty, dynastiesList, sentiusList, elementsList, rarityList, seo }) => {
  // the id of the Void and Shoi is 5 and 6, so we filter them out
  const filteredDynastiesList = dynastiesList.filter(d => d.slug !== 'shoi' && d.slug !== 'void')

  const isMobile = useMediaQuery(maxWidthQueries.sm)
  const {
    asPath,
    push: routerPush,
    query: currentQuery,
    query: {
      activeSentius: defaultActiveSentius,
      elementValue: defaultElementValue,
      number: defaultNumber,
      name: defaultName,
      sizeReference: defaultSizeRef,
      rarity: defaultRarity,
      nature: defaultNature,
      dynasty: defaultDynasty,
    },
  } = useRouter()
  const { colors } = useTheme()
  const [sentius, setSentius] = useState(sentiusList)
  const [loading, setLoading] = useState(false)
  const [activeSentius, setActiveSentius] = useState(
    defaultActiveSentius ? parseInt(defaultActiveSentius, 10) : SENTIUS_LIMIT
  )
  const [searchValue, setSearchValue] = useState(defaultNumber || defaultName || '')
  const [elementValue, setElementValue] = useState(defaultElementValue?.split(',') || [])
  const [dynastyValue, setDynastyValue] = useState(defaultDynasty || '')
  const [sizeReference, setSizeReference] = useState(defaultSizeRef || '')
  const [rarityValue, setRarityValue] = useState(defaultRarity || '')
  const [natureValue, setNatureValue] = useState(defaultNature || '')
  const [advancedSearchActive, setAdvancedSearchActive] = useState(false)

  const filterSentius = ({ number, name, elements, sizeRef, rarity, nature, dynasties }) => {
    const newSentiusList = sentiusList
    const elementsArray = elements || elementValue
    const dynastiesValue = dynasties || dynastyValue
    const size = sizeRef || sizeReference
    const rarityVal = rarity || rarityValue
    const natureVal = nature || natureValue

    return newSentiusList.filter(s => {
      const filters = []
      if (number) {
        const num = `${s.number}`
        filters.push(num.indexOf(number) >= 0)
      }
      if (name) {
        filters.push(s.name.toLowerCase().includes(name.toLowerCase()))
      }
      if (elementsArray?.length > 0) {
        filters.push(elementsArray.every(ele => s.element.find(e => e.slug === ele)))
      }
      if (size) {
        filters.push(s.sizeReference === size)
      }
      if (rarityVal) {
        filters.push(s.rarity?.slug === rarityVal)
      }
      if (natureVal) {
        filters.push(s.nature === natureVal)
      }
      if (filteredDynastiesList?.length > 0 && dynastiesValue) {
        if (s.dynasties?.length === 0) {
          filters.push(false)
        } else {
          filters.push(s.dynasties.every(e => e.slug === dynastiesValue))
        }
      }
      const hasAllFilters = filters.every(f => f === true)
      return hasAllFilters
    })
  }

  useEffect(() => {
    if (defaultActiveSentius) setActiveSentius(parseInt(defaultActiveSentius, 10))
    if (
      defaultElementValue ||
      defaultName ||
      defaultNumber ||
      defaultSizeRef ||
      defaultNature ||
      defaultRarity ||
      defaultDynasty
    ) {
      const newElemVal = defaultElementValue?.split(',')
      const newDynVal = defaultDynasty
      setSearchValue(defaultName || defaultNumber)
      setElementValue(newElemVal)
      setSizeReference(defaultSizeRef)
      setRarityValue(defaultRarity)
      setNatureValue(defaultNature)
      setDynastyValue(newDynVal)
      const filteredSentius = filterSentius({
        dynasties: newDynVal,
        elements: newElemVal,
        name: defaultName,
        nature: defaultNature,
        number: defaultNumber,
        rarity: defaultRarity,
        sizeRef: defaultSizeRef,
      })
      setSentius(filteredSentius)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    defaultDynasty,
    defaultActiveSentius,
    defaultElementValue,
    defaultName,
    defaultNature,
    defaultNumber,
    defaultRarity,
    defaultSizeRef,
  ])

  const handleClick = async () => {
    setLoading(true)
    const newSentiusLimit = activeSentius + SENTIUS_LIMIT
    await setActiveSentius(newSentiusLimit)
    const newQuery = { ...currentQuery, activeSentius: newSentiusLimit }
    routerPush({ query: newQuery }, undefined, {
      shallow: true,
    })
    setLoading(false)
  }

  const handleSubmit = async () => {
    const trimmedSearchValue = searchValue?.trim()
    setActiveSentius(0)
    setLoading(true)
    let query = '?'
    if (dynastyValue) {
      query += `dynasty=${dynastyValue}&`
    }
    if (trimmedSearchValue && trimmedSearchValue.match(NUMERIC_ONLY_REGEX)) {
      query += `number=${trimmedSearchValue}&`
    }
    if (trimmedSearchValue && !trimmedSearchValue.match(NUMERIC_ONLY_REGEX)) {
      query += `name=${trimmedSearchValue}&`
    }
    if (elementValue?.length > 0) {
      query += `elementValue=${elementValue.toString()}&`
    }
    if (sizeReference) {
      query += `sizeReference=${sizeReference}&`
    }
    if (rarityValue) {
      query += `rarity=${rarityValue}&`
    }
    if (natureValue) {
      query += `nature=${natureValue}&`
    }
    routerPush(query, undefined, { shallow: true })

    const filteredSentius = filterSentius({
      name:
        trimmedSearchValue && !trimmedSearchValue.match(NUMERIC_ONLY_REGEX) && trimmedSearchValue,
      number:
        trimmedSearchValue && trimmedSearchValue.match(NUMERIC_ONLY_REGEX) && trimmedSearchValue,
    })
    if (filteredSentius.length > 0) {
      setSentius(filteredSentius)
    } else {
      setSentius(sentiusList)
    }

    setTimeout(() => {
      setAdvancedSearchActive(false)
      setLoading(false)
      if (filteredSentius.length > 0) {
        setActiveSentius(SENTIUS_LIMIT)
      } else {
        setActiveSentius(0)
      }
    }, 200)
  }
  const handleSizeRefChange = size => {
    setSizeReference(size === sizeReference ? '' : size)
  }
  const handleElementChange = element => {
    const newElementValue = elementValue ? [...elementValue] : []
    const index = newElementValue.indexOf(element)

    if (newElementValue.length === 2 && index > -1) {
      // two already selected and user clicked on selected element
      newElementValue.splice(index, 1)
      setElementValue(newElementValue)
    } else if (newElementValue.length === 2 && index === -1) {
      // two already selected and user clicked on new element
      newElementValue.splice(1, 1)
      newElementValue.push(element)
      setElementValue(newElementValue)
    } else {
      // less than 2 selected elements already
      if (index > -1) {
        // if user clicked on the same element
        newElementValue.splice(index, 1)
      } else {
        // if user clicked on a new element9
        newElementValue.push(element)
      }
      setElementValue(newElementValue)
    }
  }

  const handleDynastyChange = newDynasty => {
    if (dynastyValue === newDynasty) {
      setDynastyValue('')
    } else {
      setDynastyValue(newDynasty)
    }
  }

  const clearSearch = () => {
    if (filteredDynastiesList) if (searchValue) setSearchValue('')
    if (elementValue) setElementValue([])
    if (dynastyValue) setDynastyValue('')
    if (sizeReference) setSizeReference('')
    if (rarityValue) setRarityValue('')
    if (natureValue) setNatureValue('')
    const newPath = asPath.split('?')[0]
    routerPush(`${newPath}`, undefined, { shallow: true })
    setSentius(sentiusList)
    setActiveSentius(SENTIUS_LIMIT)
  }

  async function getRandomSentius() {
    const randomS = await randomizeSentius(
      sentiusList,
      elementsList.map(e => e.slug)
    )
    setSentius(randomS)
  }

  return (
    <Container
      background={{ backgroundColor: colors.white }}
      justify="space-between"
      padding={isMobile ? '0rem 1rem 1rem' : '7rem 2rem 1rem'}
      position="relative"
      wrap="wrap"
    >
      <FixedBackground backgroundSize="500px" />
      <LeftRightBg />
      <Row margin="0 0 1rem">
        <Column justify="center">
          <SearchForm
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            onSubmit={e => {
              e.preventDefault()
              handleSubmit(e)
            }}
          />
        </Column>
        {/* this is the header for the dinasty header */}
        {dynasty && (
          <>
            <HeaderImage alt={`dynasty ${dynasty}`} src={`/images/dynastie/${dynasty}.png`} />
          </>
        )}
      </Row>
      <SideGlow />
      <Row justify="center" margin="0 0 2rem" position="relative">
        <AdvancedSearch
          clearSearch={clearSearch}
          dynastiesList={filteredDynastiesList}
          dynastyValue={dynastyValue}
          elementValue={elementValue}
          elementsList={elementsList}
          handleDynastyChange={handleDynastyChange}
          handleElementChange={handleElementChange}
          handleSizeRefChange={handleSizeRefChange}
          isActive={advancedSearchActive}
          natureValue={natureValue}
          randomSentius={getRandomSentius}
          rarityList={rarityList}
          rarityValue={rarityValue}
          setIsActive={setAdvancedSearchActive}
          setNatureValue={setNatureValue}
          setRarityValue={setRarityValue}
          sizeReference={sizeReference}
          onSubmit={handleSubmit}
        />
      </Row>
      <TestCardHolder align="flex-start" margin="0 0 1rem" position="relative">
        {' '}
        {sentius?.length > 0 &&
          sentius.slice(0, activeSentius).map(s => (
            <T2 key={s.id} margin="0 0 1rem" padding="0 0.5rem" width={isMobile ? '50%' : '20%'}>
              <SentiusCard
                cardImage={s.frontImage}
                dynastyFilter={dynasty}
                elements={s.element}
                name={s.name}
                number={s.number}
                slug={s.slug}
              />
            </T2>
          ))}
        {activeSentius === 0 && !loading && (
          <Column justify="center" margin="0 0 2rem">
            Uh oh! Seems like we couldn't find Sentius you were looking for.
            <br />
            <br />
            Try to change your search terms to find other awesome Sentius!
          </Column>
        )}
        <Column justify="center">
          {loading && (
            <Loader fillOne={colors.gold} fillThree={colors.teal} fillTwo={colors.black} />
          )}
          {sentius?.length > activeSentius && (
            <TealGreyButton label="Load more Sentius" onClick={handleClick} />
          )}
        </Column>
      </TestCardHolder>
    </Container>
  )
}

export default Sentipedia
